import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Globe } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { LANGS } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import api, { errMsg } from "@/lib/api";

export default function AdminLogin() {
  const { t, lang, setLang } = useLang();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => { if (user && user.role) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email is registered, a reset link has been sent.");
        setForgot(false);
      } else {
        await login(email, password);
        navigate("/admin");
      }
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div className="vl-noise" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <img src="/veloura-logo.png" alt="Veloura" className="vl-logo h-14 w-auto mx-auto" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--vl-stone)] mt-3">Admin</div>
        </Link>
        <div className="flex items-center justify-center gap-1 text-[var(--vl-stone)] mb-6" data-testid="admin-login-lang-switcher">
          <Globe size={15} className="text-[var(--vl-gold)]" />
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)}
              className={`px-2 text-xs tracking-wider uppercase transition-colors ${lang === l.code ? "text-[var(--vl-gold)]" : "hover:text-[var(--vl-ivory)]"}`} data-testid={`admin-login-lang-${l.code}`}>
              {l.code}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="vl-card rounded-2xl p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-6">
            <Lock size={18} className="text-[var(--vl-gold)]" />
            <h1 className="font-serif-display text-2xl text-[var(--vl-ivory)]">{forgot ? t("forgot_password") : t("admin_login_title")}</h1>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("email")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="vl-input rounded-lg px-4 py-3 w-full" data-testid="admin-email-input" />
            </div>
            {!forgot && (
              <div>
                <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("password")}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="vl-input rounded-lg px-4 py-3 w-full" data-testid="admin-password-input" />
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="vl-btn-gold rounded-full px-7 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2 w-full justify-center mt-7" data-testid="admin-login-submit">
            {loading && <Loader2 size={16} className="animate-spin" />} {forgot ? t("send") : t("login")}
          </button>
          <button type="button" onClick={() => setForgot(!forgot)} className="block mx-auto mt-5 text-xs text-[var(--vl-stone)] hover:text-[var(--vl-gold)] transition-colors" data-testid="toggle-forgot-btn">
            {forgot ? t("back") : t("forgot_password")}
          </button>
        </form>
      </div>
    </div>
  );
}
